import type { RouteRecordRaw } from "vue-router";
import {
  Banknote,
  BellRing,
  Building2,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  Clock3,
  Coins,
  CreditCard,
  DollarSign,
  Factory,
  HandCoins,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Package,
  ShieldCheck,
  Syringe,
  Truck,
  UsersRound,
  Waypoints,
} from "@lucide/vue";
import { getAllowedRolesForRouteName, type UserRole } from "@/router/access";

export type AppNavigationChild = {
  name: string;
  labelKey: string;
  to: string;
  icon: unknown;
};

export type AppNavigationItem = {
  name: string;
  labelKey: string;
  to: string;
  icon: unknown;
  sectionLabelKey?: string;
  badge?: string;
  children?: AppNavigationChild[];
  visibleRoles?: UserRole[];
};

const accessMeta = (routeName: string) => ({
  allowedRoles: getAllowedRolesForRouteName(routeName),
});

export const navigationItems: AppNavigationItem[] = [
  // Must stay first inside the consecutive `nav.sections.sales` run: AppSidebar groups
  // *consecutive* entries by sectionLabelKey, so anywhere further down prints a second "Sotuv"
  // header. `ChartColumn`, not `LayoutDashboard` — that one is the incubation dashboard's.
  {
    name: "sales-dashboard",
    labelKey: "nav.salesDashboard",
    to: "/sales/dashboard",
    icon: ChartColumn,
    sectionLabelKey: "nav.sections.sales",
  },
  {
    name: "customers",
    labelKey: "nav.customers",
    to: "/customers",
    icon: UsersRound,
    sectionLabelKey: "nav.sections.sales",
  },
  {
    name: "orders",
    labelKey: "nav.orders",
    to: "/orders",
    icon: ClipboardCheck,
    sectionLabelKey: "nav.sections.sales",
  },
  // Must stay adjacent to the other sales items — sections group *consecutive* entries by sectionLabelKey.
  {
    name: "reminders",
    labelKey: "nav.reminders",
    to: "/reminders",
    icon: BellRing,
    sectionLabelKey: "nav.sections.sales",
  },
  // The deliveries sales has booked from suppliers — bookings with no eggs behind them yet.
  // Labelled "Partiyalar" but coded `planned-batches`: `batches` is already the hatchery's name in
  // every layer (route, RBAC section, table), so the code keeps the qualifier the label dropped.
  {
    name: "planned-batches",
    labelKey: "nav.plannedBatches",
    to: "/sales/planned-batches",
    icon: CalendarClock,
    sectionLabelKey: "nav.sections.sales",
  },
  // Temporarily hidden from the sidebar (2026-07-30, user request). The route stays live —
  // deep links and the batch detail (`?batch=`) still resolve; restore by un-commenting, but give
  // it its own label first: `nav.eggBatches` reads "Partiyalar", the same as the entry above.
  // {
  //   name: "sales-egg-batches",
  //   labelKey: "nav.eggBatches",
  //   to: "/sales/egg-batches",
  //   icon: Package,
  //   sectionLabelKey: "nav.sections.sales",
  // },
  {
    name: "orders-hatch-calendar",
    labelKey: "nav.hatchCalendar",
    to: "/orders/hatch-calendar",
    icon: CalendarDays,
    sectionLabelKey: "nav.sections.sales",
  },
  // Last in the sales run (2026-10-10, user request). Raqobatchilar has no sidebar entry: the watch
  // list only feeds the rounds and is barely touched after the first setup, so it hangs off the
  // Narxlar screen instead (route kept, nav dropped).
  {
    name: "price-surveys",
    labelKey: "nav.pricing",
    to: "/pricing",
    icon: DollarSign,
    sectionLabelKey: "nav.sections.sales",
  },
  // MOLIYA — the money screens, between Sotuv and Inkubatsiya (2026-10-10, user request): what came
  // in, what went out, who still owes, who the company owes. Sections group *consecutive* entries
  // by sectionLabelKey, so the four stay together.
  {
    name: "payments",
    labelKey: "nav.payments",
    to: "/payments",
    icon: Banknote,
    sectionLabelKey: "nav.sections.finance",
  },
  {
    name: "expenses",
    labelKey: "nav.expenses",
    to: "/expenses",
    icon: CreditCard,
    sectionLabelKey: "nav.sections.finance",
  },
  // Xarajat turlari has no sidebar entry: the list only feeds the expense form and is barely touched
  // after the first setup, so it hangs off the Xarajatlar screen instead — the same arrangement
  // Raqobatchilar has under Narxlar (route kept, nav dropped).
  {
    name: "debtors",
    labelKey: "nav.debtors",
    to: "/debtors",
    icon: HandCoins,
    sectionLabelKey: "nav.sections.finance",
  },
  {
    name: "creditors",
    labelKey: "nav.creditors",
    to: "/creditors",
    icon: Coins,
    sectionLabelKey: "nav.sections.finance",
  },
  {
    name: "dashboard",
    labelKey: "nav.dashboard",
    to: "/incubations/dashboard",
    icon: LayoutDashboard,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "egg-batches",
    labelKey: "nav.receiving",
    to: "/egg-batches",
    icon: Package,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "sorting",
    labelKey: "nav.sorting",
    to: "/sorting",
    icon: ListChecks,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "setting-flow",
    labelKey: "nav.settingFlow",
    to: "/setting",
    icon: Clock3,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "hatching-flow",
    labelKey: "nav.hatchingFlow",
    to: "/hatching",
    icon: MapPin,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "branch-orders",
    labelKey: "nav.branchOrders",
    to: "/branch-orders",
    icon: ClipboardList,
    sectionLabelKey: "nav.sections.incubation",
  },
  {
    name: "settings-vaccines",
    labelKey: "nav.vaccines",
    to: "/settings/vaccines",
    icon: Syringe,
    sectionLabelKey: "nav.sections.settings",
  },
  {
    name: "settings-branches",
    labelKey: "nav.branches",
    to: "/settings/branches",
    icon: Building2,
    sectionLabelKey: "nav.sections.settings",
  },
  {
    name: "settings-suppliers",
    labelKey: "nav.suppliers",
    to: "/settings/suppliers",
    icon: Truck,
    sectionLabelKey: "nav.sections.settings",
  },
  {
    name: "settings-incubation-machines",
    labelKey: "nav.incubationMachines",
    to: "/settings/incubation-machines",
    icon: Factory,
    sectionLabelKey: "nav.sections.settings",
  },
  {
    name: "settings-egg-standards",
    labelKey: "nav.eggStandards",
    to: "/settings/egg-standards",
    icon: Waypoints,
    sectionLabelKey: "nav.sections.settings",
  },
  {
    name: "admin-users",
    labelKey: "nav.users",
    to: "/admin/users",
    icon: UsersRound,
    sectionLabelKey: "nav.sections.administration",
  },
  {
    name: "admin-roles",
    labelKey: "nav.roles",
    to: "/admin/roles",
    icon: ShieldCheck,
    sectionLabelKey: "nav.sections.administration",
  },
];

export const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/features/auth/AuthView.vue"),
    meta: { guestOnly: true, titleKey: "auth.signIn" },
  },
  {
    path: "/change-password",
    name: "change-password",
    component: () => import("@/features/auth/ChangePasswordView.vue"),
    // No accessMeta -> skips role gating; reachability is governed by the mustChangePassword guard.
    meta: { requiresAuth: true, titleKey: "auth.changePassword.title" },
  },
  {
    path: "/no-access",
    name: "no-access",
    component: () => import("@/features/auth/NoAccessView.vue"),
    // Reachable by any authenticated user; the landing fallback for users with no accessible route.
    meta: { requiresAuth: true, titleKey: "noAccess.title" },
  },
  {
    path: "/",
    component: () => import("@/components/layout/AppShell.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        // "/" (and the catch-all) resolve to the sales dashboard, which matches LANDING_PRIORITY[0].
        // A user who can't reach it is bounced to their landing route by the access guard.
        path: "",
        redirect: { name: "sales-dashboard" },
      },
      {
        path: "incubations/dashboard",
        name: "dashboard",
        component: () => import("@/features/dashboard/DashboardView.vue"),
        meta: { titleKey: "nav.dashboard", ...accessMeta("dashboard") },
      },
      {
        path: "sales/dashboard",
        name: "sales-dashboard",
        // The commercial read of sales (who buys, what came in). The stock read lives at
        // `sales/egg-batches`; same backend section, same permission.
        component: () =>
          import("@/features/sales-dashboard/SalesDashboardView.vue"),
        meta: {
          titleKey: "nav.salesDashboard",
          ...accessMeta("sales-dashboard"),
        },
      },
      {
        path: "sales/planned-batches",
        name: "planned-batches",
        // Sales' own bookings (`planned_batches`), not the hatchery's `batch` — different table,
        // different lifecycle. Orders bind to these.
        component: () =>
          import("@/features/planned-batches/PlannedBatchesView.vue"),
        meta: {
          titleKey: "nav.plannedBatches",
          ...accessMeta("planned-batches"),
        },
      },
      {
        path: "sales/planned-batches/new",
        name: "planned-batch-new",
        component: () =>
          import("@/features/planned-batches/PlannedBatchFormView.vue"),
        meta: {
          titleKey: "plannedBatches.form.newTitle",
          parentMenu: "planned-batches",
          ...accessMeta("planned-batch-new"),
        },
      },
      {
        path: "sales/planned-batches/:id/edit",
        name: "planned-batch-edit",
        component: () =>
          import("@/features/planned-batches/PlannedBatchFormView.vue"),
        meta: {
          titleKey: "plannedBatches.form.editTitle",
          parentMenu: "planned-batches",
          ...accessMeta("planned-batch-edit"),
        },
      },
      {
        path: "sales/egg-batches",
        name: "sales-egg-batches",
        // The sales read model of a batch (stock vs orders), not the hatchery's egg-batch screen.
        component: () =>
          import("@/features/sales-batches/SalesBatchesView.vue"),
        meta: {
          titleKey: "nav.eggBatches",
          ...accessMeta("sales-egg-batches"),
        },
      },
      {
        path: "sales/egg-batches/:id",
        name: "sales-egg-batch-detail",
        component: () =>
          import("@/features/egg-batches/EggBatchDetailView.vue"),
        meta: {
          titleKey: "eggBatches.batch",
          parentMenu: "sales-egg-batches",
          ...accessMeta("sales-egg-batch-detail"),
        },
      },
      {
        path: "orders",
        name: "orders",
        component: () => import("@/features/orders/OrdersView.vue"),
        meta: { titleKey: "nav.orders", ...accessMeta("orders") },
      },
      {
        path: "orders/hatch-calendar",
        name: "orders-hatch-calendar",
        component: () =>
          import("@/features/orders/OrdersHatchCalendarView.vue"),
        meta: {
          titleKey: "nav.hatchCalendar",
          ...accessMeta("orders-hatch-calendar"),
        },
      },
      {
        path: "orders/new",
        name: "order-new",
        component: () => import("@/features/orders/NewOrderView.vue"),
        meta: {
          titleKey: "orders.form.newTitle",
          parentMenu: "orders",
          ...accessMeta("order-new"),
        },
      },
      {
        path: "orders/:id/edit",
        name: "order-edit",
        component: () => import("@/features/orders/NewOrderView.vue"),
        meta: {
          titleKey: "orders.form.editTitle",
          parentMenu: "orders",
          ...accessMeta("order-edit"),
        },
      },
      {
        path: "orders/:id",
        name: "order-detail",
        component: () => import("@/features/orders/OrderDetailView.vue"),
        meta: {
          titleKey: "orders.detail.title",
          parentMenu: "orders",
          ...accessMeta("order-detail"),
        },
      },
      {
        // Static before ":id": the competitors screen must never resolve as a survey id.
        path: "pricing/competitors",
        name: "competitors",
        component: () => import("@/features/pricing/CompetitorsView.vue"),
        meta: {
          titleKey: "nav.competitors",
          // No nav entry of its own — Narxlar stays lit while its watch list is open.
          parentMenu: "price-surveys",
          ...accessMeta("competitors"),
        },
      },
      {
        // Market data, not the watch list — gated on `price-surveys:read`
        // (competitor-price-movement.md §4), unlike the list it is opened from.
        path: "pricing/competitors/:id",
        name: "competitor-history",
        component: () => import("@/features/pricing/CompetitorHistoryView.vue"),
        meta: {
          titleKey: "pricing.history.title",
          parentMenu: "price-surveys",
          ...accessMeta("competitor-history"),
        },
      },
      {
        path: "pricing",
        name: "price-surveys",
        component: () => import("@/features/pricing/PriceSurveysView.vue"),
        meta: { titleKey: "nav.pricing", ...accessMeta("price-surveys") },
      },
      {
        // Static before ":id", like the competitors screen above it.
        path: "pricing/history",
        name: "price-survey-history",
        component: () => import("@/features/pricing/PriceSurveyHistoryView.vue"),
        meta: {
          titleKey: "pricing.table.history",
          parentMenu: "price-surveys",
          ...accessMeta("price-survey-history"),
        },
      },
      {
        path: "pricing/new",
        name: "price-survey-new",
        component: () => import("@/features/pricing/PriceSurveyFormView.vue"),
        meta: {
          titleKey: "pricing.form.newTitle",
          parentMenu: "price-surveys",
          ...accessMeta("price-survey-new"),
        },
      },
      {
        path: "pricing/:id/edit",
        name: "price-survey-edit",
        component: () => import("@/features/pricing/PriceSurveyFormView.vue"),
        meta: {
          titleKey: "pricing.form.editTitle",
          parentMenu: "price-surveys",
          ...accessMeta("price-survey-edit"),
        },
      },
      {
        path: "pricing/:id",
        name: "price-survey-detail",
        component: () => import("@/features/pricing/PriceSurveyDetailView.vue"),
        meta: {
          titleKey: "pricing.detail.title",
          parentMenu: "price-surveys",
          ...accessMeta("price-survey-detail"),
        },
      },
      {
        path: "customers",
        name: "customers",
        component: () => import("@/features/customers/CustomersView.vue"),
        meta: { titleKey: "nav.customers", ...accessMeta("customers") },
      },
      {
        path: "customers/:id",
        name: "customer-detail",
        component: () => import("@/features/customers/CustomerDetailView.vue"),
        meta: {
          titleKey: "customers.detail.title",
          parentMenu: "customers",
          ...accessMeta("customer-detail"),
        },
      },
      {
        path: "payments",
        name: "payments",
        component: () => import("@/features/payments/PaymentsView.vue"),
        meta: { titleKey: "nav.payments", ...accessMeta("payments") },
      },
      {
        // The customer rides in the path: the detail read is
        // `/customers/{customerId}/payments/{id}/detail`, so a deep link must carry both ids.
        path: "payments/:customerId/:id",
        name: "payment-detail",
        component: () => import("@/features/payments/PaymentDetailView.vue"),
        meta: {
          titleKey: "payments.detail.pageTitle",
          parentMenu: "payments",
          ...accessMeta("payment-detail"),
        },
      },
      {
        path: "debtors",
        name: "debtors",
        component: () => import("@/features/debtors/DebtorsView.vue"),
        meta: { titleKey: "nav.debtors", ...accessMeta("debtors") },
      },
      {
        path: "creditors",
        name: "creditors",
        component: () => import("@/features/creditors/CreditorsView.vue"),
        meta: { titleKey: "nav.creditors", ...accessMeta("creditors") },
      },
      {
        // Both ids ride in the path: there is no per-transaction read, so the screen fetches the
        // customer's wallet and picks its row out of the ledger.
        path: "wallet/:customerId/:id",
        name: "wallet-transaction-detail",
        component: () => import("@/features/wallet/WalletTransactionDetailView.vue"),
        meta: {
          titleKey: "wallet.detail.pageTitle",
          parentMenu: "customers",
          ...accessMeta("wallet-transaction-detail"),
        },
      },
      {
        path: "reminders",
        name: "reminders",
        component: () => import("@/features/reminders/RemindersView.vue"),
        meta: { titleKey: "nav.reminders", ...accessMeta("reminders") },
      },
      {
        path: "expenses",
        name: "expenses",
        component: () => import("@/features/expenses/ExpensesView.vue"),
        meta: { titleKey: "nav.expenses", ...accessMeta("expenses") },
      },
      {
        path: "expenses/:id",
        name: "expense-detail",
        component: () => import("@/features/expenses/ExpenseDetailView.vue"),
        meta: {
          titleKey: "expenses.detail.title",
          parentMenu: "expenses",
          ...accessMeta("expense-detail"),
        },
      },
      {
        path: "egg-batches",
        name: "egg-batches",
        component: () => import("@/features/egg-batches/EggBatchesView.vue"),
        // Titled as its sidebar entry, "Qabul": `nav.eggBatches` reads "Partiyalar", the sales screen's name.
        meta: { titleKey: "nav.receiving", ...accessMeta("egg-batches") },
      },
      {
        path: "egg-batches/new",
        name: "egg-batch-create",
        component: () => import("@/features/egg-batches/EggBatchFormView.vue"),
        meta: {
          titleKey: "eggBatches.newBatch",
          parentMenu: "egg-batches",
          ...accessMeta("egg-batch-create"),
        },
      },
      {
        path: "egg-batches/:id",
        name: "egg-batch-detail",
        component: () =>
          import("@/features/egg-batches/EggBatchDetailView.vue"),
        meta: {
          titleKey: "eggBatches.batch",
          parentMenu: "egg-batches",
          ...accessMeta("egg-batch-detail"),
        },
      },
      {
        path: "egg-batches/:id/edit",
        name: "egg-batch-edit",
        component: () => import("@/features/egg-batches/EggBatchFormView.vue"),
        meta: {
          titleKey: "eggBatches.editBatch",
          parentMenu: "egg-batches",
          ...accessMeta("egg-batch-edit"),
        },
      },
      {
        path: "sorting",
        name: "sorting",
        component: () => import("@/features/sorting/SortingView.vue"),
        meta: { titleKey: "nav.sorting", ...accessMeta("sorting") },
      },
      {
        path: "setting",
        name: "setting-flow",
        component: () => import("@/features/incubations/SettingFlowView.vue"),
        meta: {
          titleKey: "settingAllocator.availableMachinesSelect",
          ...accessMeta("setting-flow"),
        },
      },
      {
        path: "setting/machines/:machineId",
        name: "setting-machine-detail",
        component: () =>
          import("@/features/incubations/SettingMachineDetailView.vue"),
        meta: {
          titleKey: "machinePark.detailsTitle",
          parentMenu: "setting-flow",
          ...accessMeta("setting-machine-detail"),
        },
      },
      {
        path: "setting/machines/:machineId/load",
        name: "setting-machine-load",
        component: () =>
          import("@/features/incubations/SettingMachineLoadView.vue"),
        meta: {
          titleKey: "settingAllocator.title",
          parentMenu: "setting-flow",
          ...accessMeta("setting-machine-load"),
        },
      },
      {
        path: "hatching",
        name: "hatching-flow",
        component: () => import("@/features/incubations/HatchingFlowView.vue"),
        meta: { titleKey: "nav.hatchingFlow", ...accessMeta("hatching-flow") },
      },
      {
        // The branch's view of the orders sales takes — what to hand out, and when.
        path: "branch-orders",
        name: "branch-orders",
        component: () => import("@/features/branch-orders/BranchOrdersView.vue"),
        meta: { titleKey: "nav.branchOrders", ...accessMeta("branch-orders") },
      },
      {
        path: "hatching/machines/:machineId",
        name: "hatching-machine-detail",
        component: () =>
          import("@/features/incubations/HatchingMachineDetailView.vue"),
        meta: {
          titleKey: "machinePark.detailsTitle",
          parentMenu: "hatching-flow",
          ...accessMeta("hatching-machine-detail"),
        },
      },
      {
        path: "hatching/machines/:machineId/load",
        name: "hatching-machine-load",
        component: () =>
          import("@/features/incubations/HatchingMachineLoadView.vue"),
        meta: {
          titleKey: "hatchingAllocator.loadModalTitle",
          parentMenu: "hatching-flow",
          ...accessMeta("hatching-machine-load"),
        },
      },
      {
        path: "settings",
        name: "settings",
        redirect: { name: "settings-suppliers" },
        meta: {
          titleKey: "nav.settings",
          parentMenu: "settings-suppliers",
          ...accessMeta("settings"),
        },
      },
      {
        path: "settings/branches",
        name: "settings-branches",
        component: () => import("@/features/settings/BranchesSettingsView.vue"),
        meta: {
          titleKey: "settingsAdmin.branches.title",
          parentMenu: "settings",
          ...accessMeta("settings-branches"),
        },
      },
      {
        path: "settings/suppliers",
        name: "settings-suppliers",
        component: () =>
          import("@/features/settings/SuppliersSettingsView.vue"),
        meta: {
          titleKey: "settingsAdmin.suppliers.title",
          parentMenu: "settings",
          ...accessMeta("settings-suppliers"),
        },
      },
      {
        path: "settings/incubation-machines",
        name: "settings-incubation-machines",
        component: () =>
          import("@/features/settings/IncubationMachinesSettingsView.vue"),
        meta: {
          titleKey: "settingsAdmin.incubationMachines.title",
          parentMenu: "settings",
          ...accessMeta("settings-incubation-machines"),
        },
      },
      {
        path: "settings/egg-standards",
        name: "settings-egg-standards",
        component: () =>
          import("@/features/settings/EggStandardsSettingsView.vue"),
        meta: {
          titleKey: "settingsAdmin.eggStandards.title",
          parentMenu: "settings",
          ...accessMeta("settings-egg-standards"),
        },
      },
      {
        path: "expenses/categories",
        name: "expense-categories",
        component: () =>
          import("@/features/expense-categories/ExpenseCategoriesView.vue"),
        meta: {
          titleKey: "settingsAdmin.expenseCategories.title",
          // No nav entry of its own — Xarajatlar stays lit while its category list is open.
          parentMenu: "expenses",
          ...accessMeta("expense-categories"),
        },
      },
      {
        path: "settings/vaccines",
        name: "settings-vaccines",
        component: () => import("@/features/settings/VaccinesSettingsView.vue"),
        meta: {
          titleKey: "settingsAdmin.vaccines.title",
          parentMenu: "settings",
          ...accessMeta("settings-vaccines"),
        },
      },
      {
        path: "admin/users",
        name: "admin-users",
        component: () => import("@/features/admin/UsersView.vue"),
        meta: { titleKey: "admin.users.title", ...accessMeta("admin-users") },
      },
      {
        path: "admin/users/:id",
        name: "admin-users-edit",
        component: () => import("@/features/admin/UserDetailView.vue"),
        meta: {
          titleKey: "admin.users.editTitle",
          parentMenu: "admin-users",
          ...accessMeta("admin-users-edit"),
        },
      },
      {
        path: "admin/roles",
        name: "admin-roles",
        component: () => import("@/features/admin/RolesView.vue"),
        meta: { titleKey: "admin.roles.title", ...accessMeta("admin-roles") },
      },
      {
        path: "admin/roles/:id/permissions",
        name: "admin-role-permissions",
        component: () => import("@/features/admin/RolePermissionsView.vue"),
        meta: {
          titleKey: "admin.roles.operations.title",
          parentMenu: "admin-roles",
          ...accessMeta("admin-role-permissions"),
        },
      },
      {
        path: "account",
        name: "account",
        component: () => import("@/features/account/AccountView.vue"),
        // Self-service profile — reachable by any authenticated user (no accessMeta; see access.ts).
        meta: { titleKey: "account.title" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];
